import type { ArtifactListingEntry } from '@crowdex/agent-contracts/api';
import { useMemo } from 'react';
import { useArtifactIndex } from '@/features/apps/use-artifact-index';
import type { PluginWorkbench } from '@/features/plugins/plugin-workbench';
import { useWorkbench } from '@/features/plugins/use-plugin-workbench';
import {
  appMentionTarget,
  artifactMentionTarget,
  type MentionTarget,
} from '@/lib/side-panel/mention-targets';

/** The installed apps as @ references: each enabled app, by its title. */
export function appMentionTargets(
  workbench: Pick<PluginWorkbench, 'enabledPlugins' | 'pluginDisplayNames' | 'pluginSkills'> & {
    panels: readonly { plugin: string; description?: string | undefined }[];
  },
): MentionTarget[] {
  return workbench.enabledPlugins.map((plugin) =>
    appMentionTarget({
      plugin,
      title: workbench.pluginDisplayNames[plugin] ?? plugin,
      description: workbench.panels.find((panel) => panel.plugin === plugin && panel.description)
        ?.description,
      skills: workbench.pluginSkills[plugin] ?? [],
    }),
  );
}

/**
 * The references @ offers besides the conversation's own: its targets, then the artifacts of the
 * user's library it does not list already, then the installed apps. Neither needs a repository.
 */
export function withLibraryTargets(
  targets: readonly MentionTarget[],
  library: readonly ArtifactListingEntry[],
  apps: readonly MentionTarget[],
): MentionTarget[] {
  const keys = new Set(targets.map((target) => `${target.kind}:${target.id}`));
  return [
    ...targets,
    ...library
      .map(artifactMentionTarget)
      .filter((target) => !keys.has(`${target.kind}:${target.id}`)),
    ...apps.filter((target) => !keys.has(`${target.kind}:${target.id}`)),
  ];
}

/**
 * `targets` with the user's library and installed apps, read while `enabled` (the picker lists
 * references: @, or a search of the +).
 */
export function useMentionLibrary(
  targets: readonly MentionTarget[],
  enabled: boolean,
): readonly MentionTarget[] {
  const workbench = useWorkbench();
  const { artifacts } = useArtifactIndex(undefined, { enabled });
  const apps = useMemo(() => appMentionTargets(workbench), [workbench]);
  return useMemo(
    () => (enabled ? withLibraryTargets(targets, artifacts, apps) : targets),
    [apps, artifacts, enabled, targets],
  );
}
